import { useEffect } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { Navigate, useNavigate } from 'react-router-dom';
import { Loading } from '@/shared/ui/loading/routeLoading.tsx';
import { CustomersStepTypeEnum } from '@/shared/endpoints/customers/models/enums';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { selectIsAuthorized } from '@/store/slices/auth/selectors.ts';
import { selectUserOnboarding } from '@/store/slices/onboarding/selectors.ts';
import { ROUTES } from '@/shared/constants/routes';
import { onLoadSetupThunk } from './routingInitHelpers';
import { consolidatedRouteLibraries } from './shared/constants/main';
import { useInitializationPendingWork } from './shared/hooks/pendingWork';
import { isUnderForty } from './shared/helpers/main';

export type PageType = 'default' | 'onboarding' | 'thankYou' | 'uneligible';
export interface ConsolidatedRouteHandlerProps {
    Component?: React.ComponentType;
    pageType?: PageType;
}

const completedStates = new Set([CustomersStepTypeEnum.ThankYou, CustomersStepTypeEnum.Done]);


const ConsolidatedRouteHandler: React.FC<ConsolidatedRouteHandlerProps> = ({
    Component,
    pageType,
    ...rest
}) => {
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: import.meta.env.VITE_APP_GOOGLE_API_KEY,
        libraries: consolidatedRouteLibraries
    });
    const isAuthorized = useAppSelector(selectIsAuthorized);
    const {
        user,
        accountStatus: { loading, error, complete }
    } = useAppSelector(selectUserOnboarding);

    const hasPendingWork = useInitializationPendingWork();

    const userOnboarding = user?.userOnboarding;
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (isAuthorized && !error && !loading && !complete && isLoaded) {
            dispatch(onLoadSetupThunk());
        }
    }, [navigate, dispatch, isAuthorized, error, complete, isLoaded, loading]);

    const gmtLoading = !isLoaded && !loadError;
    const setupThunkLoading = !complete && !error;
    const loadingLocal = loading || gmtLoading || setupThunkLoading || hasPendingWork;
    const localError = error || loadError;
    const { currentStep } = userOnboarding?.details || {};
    const profileComplete = currentStep && completedStates.has(currentStep) ? currentStep : '';
    const goingToDashBoard = profileComplete === CustomersStepTypeEnum.Done;

    useEffect(() => {
        if (isAuthorized && localError) {
            throw localError;
        }
    }, [localError, isAuthorized]);

    if (!isAuthorized) {
        return <Navigate to={ROUTES.LOGIN} />;
    }

    if (localError) {
        return null;
    }

    if (loadingLocal) {
        return <Loading />;
    }

    // if they have not entered a dob this eligibility will not be available
    const eligible = !user?.userProfile?.dob || !isUnderForty(user?.userProfile?.dob || '');

    if (pageType === 'default') {
        if (!eligible && currentStep !== CustomersStepTypeEnum.ThankYou && !goingToDashBoard) {
            return <Navigate to={ROUTES.NOT_ELIGIBLE} />
        }

        if (currentStep === CustomersStepTypeEnum.ThankYou) {
            return <Navigate to={ROUTES.THANK_YOU} />
        } else if (goingToDashBoard) {
            return <Navigate to={'/' + ROUTES.DASHBOARD.MAIN} />;
        } else {
            return <Navigate to={'/' + ROUTES.ONBOARDING.MAIN} />;
        }
    }

    if (pageType === 'uneligible') {
        if (goingToDashBoard) {
            return <Navigate to={'/' + ROUTES.DASHBOARD.MAIN} />;
        }
        if (eligible) {
            return <Navigate to={'/' + ROUTES.ONBOARDING.MAIN} />;
        } else {
            return Component ? <Component {...rest} /> : null;
        }
    }

    if (pageType === 'onboarding') {
        // if we are in onboarding handler but have determined we can go to dashboard go to dashboard
        if (goingToDashBoard) {
            return <Navigate to={'/' + ROUTES.DASHBOARD.MAIN} />;
        } else if (currentStep === CustomersStepTypeEnum.ThankYou) {
            return <Navigate to={ROUTES.THANK_YOU} />
        }
    } else if (!goingToDashBoard && pageType !== 'thankYou') {
        // if we are in dashboard handler but have determined we cannot go to dashboard go back to onboarding
        return <Navigate to={'/' + ROUTES.ONBOARDING.MAIN} />;
    } else if (pageType === 'thankYou') {
        if (!goingToDashBoard && currentStep !== CustomersStepTypeEnum.ThankYou) {
            return <Navigate to={'/' + ROUTES.ONBOARDING.MAIN} />;
        }
    }

    return Component ? <Component {...rest} /> : null;
};

export default ConsolidatedRouteHandler;
